import api from "@/services/api"

const MAX_LABEL_LENGTH = 80
const MAX_ROUTE_LENGTH = 240
const MAX_MESSAGE_LENGTH = 240
const MAX_STACK_LINES = 5
const MAX_STACK_LINE_LENGTH = 120
const MAX_STACK_SUMMARY_LENGTH = 480
const MAX_BROWSER_FIELD_LENGTH = 160
const MAX_PAYLOAD_LENGTH = 2048
const REDACTED_MESSAGE = "Redacted client runtime error"
const SENSITIVE_ROUTE_QUERY_KEY_PATTERN =
  /(token|passw(?:or)?d|passwd|secret|authorization|auth[_-]?token|api[_-]?key|session|cookie|bearer)/i
const SENSITIVE_PATTERN =
  /(authorization|bearer\s+[a-z0-9._-]+|token|password|passwd|secret|cookie|set-cookie|localstorage|sessionstorage|sqlstate|select\s+.+\s+from|insert\s+into|update\s+\w+\s+set|delete\s+from|request\s*body|response\s*body)/i

/**
 * Normalize one scalar label to a bounded alphanumeric diagnostic token.
 *
 * @precondition value may be any runtime value.
 * @postcondition Returns an empty string or a bounded token safe for transport/logging.
 * @mutates none
 * @sideeffects none
 */
function sanitizeLabel(value, maxLength = MAX_LABEL_LENGTH) {
  const label = typeof value === "string" ? value.trim() : ""
  if (!label) return ""

  const normalized = label.replace(/[^a-z0-9_.:-]/gi, "").slice(0, maxLength)
  return normalized
}

/**
 * Normalize one route/path value without exposing unrelated browser state.
 *
 * @precondition value may be any runtime value.
 * @postcondition Returns an empty string or a bounded route-like string.
 * @mutates none
 * @sideeffects none
 */
function sanitizeRoute(value) {
  const route = typeof value === "string" ? value.trim() : ""
  if (!route) return ""

  const [routeWithQuery, hashFragment = ""] = route.split("#", 2)
  const [rawPath = "", rawQuery = ""] = routeWithQuery.split("?", 2)
  const path = rawPath.replace(/[^a-z0-9/_.:%-]/gi, "")
  const safeQuery = rawQuery
    .split("&")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [rawKey = "", ...rawValueParts] = entry.split("=")
      const key = rawKey.replace(/[^a-z0-9_.:%-]/gi, "")
      if (!key || SENSITIVE_ROUTE_QUERY_KEY_PATTERN.test(key)) {
        return ""
      }

      void rawValueParts
      return key
    })
    .filter(Boolean)
    .join("&")
  const hash = hashFragment.replace(/[^a-z0-9/_.:%-]/gi, "")

  return `${path}${safeQuery ? `?${safeQuery}` : ""}${hash ? `#${hash}` : ""}`.slice(
    0,
    MAX_ROUTE_LENGTH
  )
}

/**
 * Collapse a possibly sensitive free-form message into one safe bounded string.
 *
 * @precondition value may be any runtime value.
 * @postcondition Returns an empty string, a truncated safe message, or the generic redacted message.
 * @mutates none
 * @sideeffects none
 */
function sanitizeMessage(value) {
  const message = typeof value === "string" ? value.trim() : ""
  if (!message) return ""
  if (SENSITIVE_PATTERN.test(message)) return REDACTED_MESSAGE

  return message.replace(/[\r\n\t]+/g, " ").slice(0, MAX_MESSAGE_LENGTH)
}

/**
 * Summarize one runtime stack without leaking raw payload/body/token-like data.
 *
 * @precondition stack may be any runtime value.
 * @postcondition Returns one bounded summary string containing only safe lines.
 * @mutates none
 * @sideeffects none
 */
function sanitizeStackSummary(stack) {
  if (typeof stack !== "string" || !stack.trim()) return ""

  const safeLines = stack
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !SENSITIVE_PATTERN.test(line))
    .slice(0, MAX_STACK_LINES)
    .map((line) => line.slice(0, MAX_STACK_LINE_LENGTH))

  return safeLines.join(" | ").slice(0, MAX_STACK_SUMMARY_LENGTH)
}

/**
 * Produce one stable bounded hash for the sanitized stack summary.
 *
 * @precondition value is already a safe bounded string.
 * @postcondition Returns an eight-character lowercase hexadecimal digest.
 * @mutates none
 * @sideeffects none
 */
function hashSummary(value) {
  let hash = 2166136261
  const source = String(value || "")

  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }

  return (hash >>> 0).toString(16).padStart(8, "0")
}

/**
 * Extract one safe support/request identifier from the original failing context.
 *
 * @precondition error is an error-like object from browser/runtime or axios transport.
 * @postcondition Returns one bounded request id or an empty string.
 * @mutates none
 * @sideeffects none
 */
function extractRequestId(error) {
  const candidates = [
    error?.response?.data?.meta?.request_id,
    error?.response?.headers?.["x-request-id"],
    error?.response?.headers?.["X-Request-ID"],
    error?.config?.headers?.["X-Request-ID"],
    error?.config?.headers?.["x-request-id"],
  ]

  for (const candidate of candidates) {
    const normalized = sanitizeLabel(candidate, 128)
    if (/^[a-z0-9][a-z0-9._:-]{7,127}$/i.test(normalized)) {
      return normalized
    }
  }

  return ""
}

/**
 * Capture the current browser route without touching storage or cookies.
 *
 * @postcondition Returns one bounded route string or an empty string when window is unavailable.
 * @mutates none
 * @sideeffects none
 */
function currentRoute() {
  if (typeof window === "undefined" || !window.location) return ""

  const route = `${window.location.pathname || ""}${window.location.search || ""}${window.location.hash || ""}`
  return sanitizeRoute(route)
}

/**
 * Build one allowlisted browser-info object for the client error sink.
 *
 * @postcondition Returns only user_agent, language, and platform keys when available.
 * @mutates none
 * @sideeffects none
 */
function buildBrowserInfo() {
  if (typeof navigator === "undefined") return {}

  const browser = {
    user_agent:
      typeof navigator.userAgent === "string"
        ? navigator.userAgent.slice(0, MAX_BROWSER_FIELD_LENGTH)
        : "",
    language:
      typeof navigator.language === "string"
        ? navigator.language.slice(0, MAX_BROWSER_FIELD_LENGTH)
        : "",
    platform:
      typeof navigator.platform === "string"
        ? navigator.platform.slice(0, MAX_BROWSER_FIELD_LENGTH)
        : "",
  }

  return Object.fromEntries(
    Object.entries(browser).filter(([, value]) => value)
  )
}

/**
 * Remove empty fields and enforce a bounded payload size.
 *
 * @precondition payload contains only safe allowlisted fields.
 * @postcondition Returns a bounded payload or null when nothing safe remains.
 * @mutates none
 * @sideeffects none
 */
function finalizePayload(payload) {
  const compact = Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => {
      if (value === null || value === undefined) return false
      if (typeof value === "string") {
        return value.length > 0 || key === "stack_summary"
      }
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === "object") return Object.keys(value).length > 0
      return true
    })
  )

  if (Object.keys(compact).length === 0) return null

  if (JSON.stringify(compact).length <= MAX_PAYLOAD_LENGTH) {
    return compact
  }

  if (compact.stack_summary) {
    compact.stack_summary = String(compact.stack_summary).slice(0, 160)
  }

  if (JSON.stringify(compact).length <= MAX_PAYLOAD_LENGTH) {
    return compact
  }

  return null
}

/**
 * Build the first-party sanitized client-error payload expected by the backend sink.
 *
 * @precondition error is an error-like runtime object and context is optional metadata.
 * @postcondition Returns only allowlisted safe fields or null when no safe payload can be formed.
 * @mutates none
 * @sideeffects none
 */
export function buildClientErrorPayload(error, context = {}) {
  const stackSummary = sanitizeStackSummary(error?.stack)
  const payload = {
    request_id: extractRequestId(error),
    route: sanitizeRoute(context.route || currentRoute()),
    component: sanitizeLabel(context.component),
    action: sanitizeLabel(context.action || context.info),
    error_name: sanitizeLabel(error?.name || "Error"),
    message: sanitizeMessage(
      error?.message || context.message || REDACTED_MESSAGE
    ),
    stack_hash: stackSummary ? hashSummary(stackSummary) : "",
    stack_summary: stackSummary,
    browser: buildBrowserInfo(),
  }

  return finalizePayload(payload)
}

/**
 * Report one sanitized Editor runtime error to the first-party backend sink.
 *
 * @precondition Uses the shared api service layer and must never read storage/cookies directly.
 * @postcondition Returns true only for safe 2xx sink responses; returns false for invalid payloads or transport failures.
 * @mutates none
 * @sideeffects Sends one POST request to /observability/client-error when a safe payload exists.
 */
export async function reportClientRuntimeError(error, context = {}) {
  const payload = buildClientErrorPayload(error, context)
  if (!payload) return false

  try {
    const response = await api.post("/observability/client-error", payload, {
      timeout: 5000,
      validateStatus: () => true,
    })

    return response?.status >= 200 && response?.status < 300
  } catch (_reporterError) {
    return false
  }
}

export default {
  buildClientErrorPayload,
  reportClientRuntimeError,
}
